import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { QuillTemplateImagePreviewContext } from '@phoenix/common';
import { AuthService } from '../../services/auth.service';
import { TenantService } from '../../services/tenant.service';
import { PhxRichTextComponent } from '../phx-rich-text/phx-rich-text.component';

/**
 * Formly wrapper around PhxRichTextComponent.
 *
 * Bridges:
 *  - formly's `this.formControl`  →  [formControl] on the base component
 *  - formly form's `previewMode` field  ↔  [(isPreviewMode)] on the base component
 *  - formly form's `emailTemplate` field  →  forces preview ON when a template loads
 *
 * All visual/interaction logic lives in PhxRichTextComponent.
 * Use `phx-rich-text` directly when formly is not available.
 */
@Component({
  selector: 'app-formly-rich-text-field-component',
  template: `
    <phx-rich-text
      [formControl]="formControl"
      [isPreviewMode]="isPreviewMode()"
      (isPreviewModeChange)="onPreviewModeChange($event)"
      [templatePreviewContext]="templatePreviewContext()"
    ></phx-rich-text>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PhxRichTextComponent],
})
export class FormlyRichTextFieldComponent extends FieldType {
  private destroyRef = inject(DestroyRef);
  private tenantService = inject(TenantService);
  private authService = inject(AuthService);

  isPreviewMode = signal<boolean>(true);
  templatePreviewContext = signal<QuillTemplateImagePreviewContext | null>(null);

  constructor() {
    super();
    afterNextRender({
      write: () => {
        //this still seems a little hacky
        this.isPreviewMode.set(this.form.get('previewMode')?.value ?? true);

        this.form.get('previewMode')?.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((value: boolean) => this.isPreviewMode.set(value));

        this.refreshTemplatePreviewContext();

        // Force preview ON the first time a template is selected
        // this.form.get('emailTemplate')?.valueChanges.pipe(
        //   take(1),
        //   filter(value => !!value?.id),
        //   delay(500),
        // ).subscribe(() => {
        //   this.isPreviewMode.set(true);
        //   this.form.get('previewMode')?.setValue(true);
        // });
      },
    });
  }

  onPreviewModeChange(newValue: boolean): void {
    this.isPreviewMode.set(newValue);
    this.form.get('previewMode')?.setValue(newValue);
  }

  private refreshTemplatePreviewContext(): void {
    const tenant = this.tenantService.getActiveTenantFromCache();
    const user = this.authService.getCurrentUser();
    this.templatePreviewContext.set({
      tenant,
      user,
      employee: user?.activeEmployee ?? null,
      entity: null,
    });
  }
}
